import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useApi } from '@/api/client';
import { useFocusTrap } from '@/components/Dialog';
import { isDecisionsModel } from '@/components/DecisionsBadge';
import { navPages } from '@/components/navItems';
import type { Me } from '@/types';

interface Entry {
  id: string;
  section: 'Pages' | 'Models' | 'Callers';
  label: string;
  sub?: string;
  kind?: string;
  to: string;
}

/**
 * Ctrl+K "jump to": pages, models and (for admins) callers, filtered as you type.
 *
 * A combobox over a listbox: focus stays in the input, arrow keys move the active option
 * (announced via aria-activedescendant), Enter opens it, Escape closes. The model and caller
 * lists are only fetched while the palette is open, and reuse the pages' own cache entries.
 */
export function JumpPalette({ me, onClose }: { me: Me; onClose: () => void }) {
  const api = useApi();
  const navigate = useNavigate();
  const ref = useFocusTrap(onClose);
  const listId = useId();
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const listRef = useRef<HTMLUListElement | null>(null);

  const adminModels = useQuery({ queryKey: ['admin-models'], queryFn: api.adminListModels, enabled: me.isAdmin });
  const models = useQuery({ queryKey: ['models'], queryFn: api.listModels, enabled: !me.isAdmin });
  const callers = useQuery({
    queryKey: ['admin-callers', 'All'],
    queryFn: () => api.adminListCallers(),
    enabled: me.isAdmin
  });

  const entries = useMemo<Entry[]>(() => {
    const out: Entry[] = navPages
      .filter(p => !p.adminOnly || me.isAdmin)
      .map(p => ({ id: `page:${p.to}`, section: 'Pages', label: p.label, sub: p.group, to: p.to }));

    if (me.isAdmin) {
      for (const m of adminModels.data ?? []) {
        out.push({
          id: `model:${m.id}`, section: 'Models', label: m.displayName, sub: m.modelId,
          kind: isDecisionsModel(m) ? 'Decisions' : m.supplier, to: `/admin/models/${m.id}`
        });
      }
      for (const c of callers.data ?? []) {
        out.push({
          id: `caller:${c.id}`, section: 'Callers', label: c.displayName,
          sub: c.email ?? c.apimSubscriptionName ?? undefined, kind: c.status, to: `/admin/callers#caller-${c.id}`
        });
      }
    } else {
      // Callers without admin rights can't open a model page, so a model jumps to the
      // Playground with that model already picked.
      for (const m of models.data ?? []) {
        const decisions = isDecisionsModel(m);
        const qs = new URLSearchParams(decisions
          ? { mode: 'decisions', model: m.modelId }
          : { model: `${m.supplier}::${m.modelId}` });
        out.push({
          id: `model:${m.supplier}:${m.modelId}`, section: 'Models', label: m.displayName, sub: m.modelId,
          kind: decisions ? 'Decisions' : m.supplier, to: `/playground?${qs.toString()}`
        });
      }
    }
    return out;
  }, [me.isAdmin, adminModels.data, models.data, callers.data]);

  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    const matched = q === ''
      ? entries.filter(e => e.section === 'Pages')
      : entries.filter(e => `${e.label} ${e.sub ?? ''} ${e.kind ?? ''}`.toLowerCase().includes(q));
    return matched.slice(0, 60);
  }, [entries, query]);

  useEffect(() => { setActive(0); }, [query]);

  // Keep the active option in view as the arrow keys move it.
  useEffect(() => {
    const el = listRef.current?.querySelector<HTMLElement>(`[data-index="${active}"]`);
    el?.scrollIntoView({ block: 'nearest' });
  }, [active]);

  const go = (entry: Entry | undefined) => {
    if (!entry) return;
    onClose();
    navigate(entry.to);
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActive(i => (results.length === 0 ? 0 : (i + 1) % results.length));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive(i => (results.length === 0 ? 0 : (i - 1 + results.length) % results.length));
    } else if (e.key === 'Home') {
      e.preventDefault();
      setActive(0);
    } else if (e.key === 'End') {
      e.preventDefault();
      setActive(Math.max(0, results.length - 1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      go(results[active]);
    }
  };

  const loading = (me.isAdmin ? adminModels.isLoading || callers.isLoading : models.isLoading) && query.trim() !== '';
  const optionId = (i: number) => `${listId}-opt-${i}`;

  let lastSection = '';
  return createPortal(
    <div className="dialog-scrim" onMouseDown={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div ref={ref} className="dialog palette" role="dialog" aria-modal="true" aria-label="Jump to a page, model or caller">
        <input
          className="palette-input"
          data-autofocus
          type="text"
          role="combobox"
          aria-expanded="true"
          aria-controls={listId}
          aria-autocomplete="list"
          aria-activedescendant={results.length > 0 ? optionId(active) : undefined}
          aria-label={me.isAdmin ? 'Jump to a page, model or caller' : 'Jump to a page or model'}
          placeholder={me.isAdmin ? 'Jump to a page, model or caller…' : 'Jump to a page or model…'}
          value={query}
          onChange={e => setQuery(e.target.value)}
          onKeyDown={onKeyDown}
        />
        <ul className="palette-list" id={listId} role="listbox" ref={listRef} aria-label="Results">
          {results.map((r, i) => {
            const header = r.section !== lastSection ? r.section : null;
            lastSection = r.section;
            return (
              <li key={r.id} role="presentation">
                {header && <div className="palette-section" role="presentation">{header}</div>}
                <div
                  id={optionId(i)}
                  data-index={i}
                  role="option"
                  aria-selected={i === active}
                  className="palette-item"
                  onMouseMove={() => setActive(i)}
                  onClick={() => go(r)}>
                  <span>{r.label}</span>
                  {r.sub && <span className="palette-sub">{r.sub}</span>}
                  {r.kind && <span className="palette-kind">{r.kind}</span>}
                </div>
              </li>
            );
          })}
        </ul>
        {results.length === 0 && (
          <div className="palette-empty">{loading ? 'Loading…' : `Nothing matches “${query}”.`}</div>
        )}
        <div className="palette-foot" aria-hidden="true">
          <span><kbd>↑</kbd> <kbd>↓</kbd> move</span>
          <span><kbd>Enter</kbd> open</span>
          <span><kbd>Esc</kbd> close</span>
        </div>
      </div>
    </div>,
    document.body
  );
}
